import { format } from 'date-fns';
import { addDoc, collection, doc, Timestamp, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, CategoryPicker, DatePicker, Input, LoadingSpinner, MapboxGeocoder } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { cityService, type City } from '../services/cityService';
import type { Event, LocationData } from '../services/eventService';
import { db, storage } from '../utils/firebaseConfig';
import { trackEventCreationStarted, trackEventCreated } from '../services/analyticsService';

export function AddEventPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const errorRef = React.useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dateRange: { from: undefined as Date | undefined, to: undefined as Date | undefined },
    startTime: '',
    endTime: '',
    category: '',
    isFree: true,
    cost: 0,
    tags: [] as string[],
    image: null as File | null,
    website: ''
  });

  const [location, setLocation] = useState<LocationData>();
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [linkPreview, setLinkPreview] = useState<{
    title?: string;
    description?: string;
    favicon?: string;
    image?: string;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [timeError, setTimeError] = useState('');

  // Track page view and fetch cities on mount
  useEffect(() => {
    trackEventCreationStarted();

    const loadCities = async () => {
      try {
        const fetchedCities = await cityService.fetchCities();
        setCities(fetchedCities);
      } catch (error) {
        console.error('Error loading cities:', error);
        setError('Failed to load cities. Please refresh the page.');
      }
    };
    loadCities();
  }, []);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const handleLocationSelect = async (location: LocationData) => {
    // Try geofencing first if coordinates are available
    let matchedCity = await cityService.findCityByCoordinates(
      location.coordinates.latitude,
      location.coordinates.longitude
    );

    // Fallback to string matching if geofencing didn't work
    if (!matchedCity) {
      matchedCity = await cityService.findCityByLocation(location.city);
    }

    if (!matchedCity) {
      const cityNames = cities.map(c => c.name).join(' or ');
      setError(`Events can only be added in ${cityNames}`);
      setLocation(undefined);
      setSelectedCity(null);
      return;
    }

    setError('');
    setLocation(location);
    setSelectedCity(matchedCity);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper to normalize URL by adding https:// if missing
  const normalizeUrl = (url: string): string => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return '';

    // If it already has a protocol, return as-is
    if (/^https?:\/\//i.test(trimmedUrl)) {
      return trimmedUrl;
    }

    // Add https:// if missing
    return `https://${trimmedUrl}`;
  };

  // Helper to validate URL format
  const isValidUrl = (url: string) => {
    if (!url) return false;

    // Normalize URL first
    const normalized = normalizeUrl(url);

    try {
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  };

  // Function to fetch website link preview
  const fetchLinkPreview = async (url: string) => {
    if (!url || !isValidUrl(url)) {
      setLinkPreview(null);
      return;
    }

    // Normalize the URL for fetching
    const normalizedUrl = normalizeUrl(url);

    setPreviewLoading(true);
    try {
      // Using a link preview API service
      const apiUrl = `https://api.linkpreview.net?q=${encodeURIComponent(normalizedUrl)}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'X-Linkpreview-Api-Key': 'aff3406ea2a89aae35c97731d13910fc'
        }
      });
      const data = await response.json();

      if (data && !data.error) {
        setLinkPreview({
          title: data.title,
          description: data.description,
          favicon: data.image,
          image: data.image
        });
      } else {
        // Fallback: extract basic info from URL
        const urlObj = new URL(normalizedUrl);
        setLinkPreview({
          title: urlObj.hostname,
          description: normalizedUrl,
          favicon: `${urlObj.protocol}//${urlObj.host}/favicon.ico`
        });
      }
    } catch (error) {
      console.error('Error fetching link preview:', error);
      // Fallback: extract basic info from URL
      try {
        const urlObj = new URL(normalizedUrl);
        setLinkPreview({
          title: urlObj.hostname,
          description: normalizedUrl,
          favicon: `${urlObj.protocol}//${urlObj.host}/favicon.ico`
        });
      } catch {
        setLinkPreview(null);
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  // Debounced link preview fetching
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.website) {
        fetchLinkPreview(formData.website);
      } else {
        setLinkPreview(null);
      }
    }, 1000); // Wait 1 second after user stops typing

    return () => clearTimeout(timeoutId);
  }, [formData.website]);

  // Validate times based on date range
  useEffect(() => {
    if (formData.startTime && formData.endTime && formData.dateRange.from) {
      const isSingleDay = !formData.dateRange.to ||
        formData.dateRange.from?.toDateString() === formData.dateRange.to?.toDateString();

      if (isSingleDay && formData.endTime <= formData.startTime) {
        setTimeError('End time must be after start time for single-day events');
      } else {
        setTimeError('');
      }
    } else {
      setTimeError('');
    }
  }, [formData.startTime, formData.endTime, formData.dateRange]);

  const uploadImage = async (file: File, eventId: string): Promise<string> => {
    const filename = `events/${eventId}/${Date.now()}_${file.name}`;
    const imageRef = ref(storage, filename);
    const snapshot = await uploadBytes(imageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError('');
    setLoading(true);

    try {
      if (!formData.title.trim()) {
        throw new Error('Event title is required');
      }
      if (!formData.description.trim()) {
        throw new Error('Event description is required');
      }
      if (!formData.dateRange.from) {
        throw new Error('Event start date is required');
      }
      if (!formData.startTime) {
        throw new Error('Event start time is required');
      }
      if (!formData.endTime) {
        throw new Error('Event end time is required');
      }

      // Validate time logic based on date range
      const isSingleDay = !formData.dateRange.to ||
        formData.dateRange.from?.toDateString() === formData.dateRange.to?.toDateString();

      if (isSingleDay) {
        // For single-day events, end time must be after start time
        if (formData.endTime <= formData.startTime) {
          throw new Error('End time must be after start time for single-day events');
        }
      }

      if (!formData.category) {
        throw new Error('Event category is required');
      }
      if (!location) {
        throw new Error('Event location is required');
      }
      if (!selectedCity) {
        throw new Error('Please select a valid location in one of our supported cities');
      }

      // Format dates to YYYY-MM-DD using date-fns to avoid timezone issues
      const formatDateToISO = (date: Date): string => {
        return format(date, 'yyyy-MM-dd');
      };

      // Use link preview image as fallback if no image uploaded
      let initialImageUrl = '';
      if (!formData.image && linkPreview?.image) {
        initialImageUrl = linkPreview.image;
      }

      const eventData: Omit<Event, 'id'> = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        startDate: formatDateToISO(formData.dateRange.from!),
        endDate: formData.dateRange.to ? formatDateToISO(formData.dateRange.to) : formatDateToISO(formData.dateRange.from!),
        startTime: formData.startTime,
        endTime: formData.endTime,
        location: location,
        city: selectedCity,
        category: formData.category,
        isFree: formData.isFree,
        cost: formData.isFree ? 0 : formData.cost,
        tags: formData.tags,
        image: initialImageUrl,
        website: formData.website ? normalizeUrl(formData.website) : null,
        linkPreview: linkPreview || null,
        createdBy: user.uid,
        createdByName: user.displayName || user.email || 'Unknown User',
        createdByAvatar: user.photoURL || undefined,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        interested: 0
      };

      // Create the event first to get the ID
      const docRef = await addDoc(collection(db, 'events'), eventData);

      // If there's an image to upload, upload it now with the event ID
      if (formData.image) {
        const imageUrl = await uploadImage(formData.image, docRef.id);
        await updateDoc(doc(db, 'events', docRef.id), { image: imageUrl });
      }

      // Track event creation
      trackEventCreated({
        event_id: docRef.id,
        category: formData.category,
        is_free: formData.isFree,
        has_image: !!formData.image || !!linkPreview?.image,
        has_website: !!formData.website,
        tag_count: formData.tags.length,
        city: selectedCity?.name,
      });

      navigate(`/events/${docRef.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-0 pb-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Create New Event</h1>
        <p className="text-gray-600 mt-2">Share your event with the community</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div ref={errorRef} className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Event Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Event Title *
            </label>
            <Input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter event title"
              required
              className="w-full"
            />
          </div>

          {/* Event Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe your event..."
              required
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
          </div>

          {/* Date Range and Category Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                Event Date Range *
              </label>
              <DatePicker
                value={formData.dateRange}
                onChange={(dateRange) => setFormData(prev => ({
                  ...prev,
                  dateRange: {
                    from: dateRange.from,
                    to: dateRange.to
                  }
                }))}
                placeholder="Select event date range"
                className="w-full"
                minDate={new Date()}
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <CategoryPicker
                value={formData.category}
                onChange={(category) => setFormData(prev => ({ ...prev, category }))}
                placeholder="Select category"
                className="w-full"
              />
            </div>
          </div>

          {/* Time Row */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time *
                </label>
                <Input
                  id="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                  placeholder="Select start time"
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-2">
                  End Time *
                </label>
                <Input
                  id="endTime"
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                  placeholder="Select end time"
                  required
                  className="w-full"
                />
              </div>
            </div>
            {timeError && (
              <p className="mt-2 text-sm text-red-600">{timeError}</p>
            )}
          </div>

          {/* Location with Mapbox */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
              Location *
            </label>
            {cities.length > 0 && (
              <p className="text-sm text-gray-500 mb-2">
                Events must be located in {cities.map(c => c.name).join(' or ')}
              </p>
            )}
            <MapboxGeocoder
              placeholder="Search for event location..."
              onLocationSelect={handleLocationSelect}
              className="w-full"
            />
            {location && selectedCity && (
              <p className="text-sm text-green-600 mt-2">
                ✓ {location.name} ({selectedCity.name})
              </p>
            )}
          </div>

          {/* Pricing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Event Pricing
            </label>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="pricing"
                  checked={formData.isFree}
                  onChange={() => setFormData(prev => ({ ...prev, isFree: true, cost: 0 }))}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Free Event</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="radio"
                  name="pricing"
                  checked={!formData.isFree}
                  onChange={() => setFormData(prev => ({ ...prev, isFree: false }))}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Paid Event</span>
              </label>

              {!formData.isFree && (
                <div className="ml-6 mt-2">
                  <Input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: Number(e.target.value) }))}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-32"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Website */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
              Website (optional)
            </label>
            <Input
              id="website"
              type="text"
              value={formData.website}
              onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
              placeholder="example.com or https://example.com"
              className="w-full"
            />
            {(linkPreview || previewLoading) && (
              <div className="mt-3">
                {previewLoading ? (
                  <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-600">Loading preview...</span>
                  </div>
                ) : linkPreview && (
                  <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                    <div className="p-4 flex gap-4 items-center">
                      {/* Preview Image - Small rectangle on the side */}
                      {linkPreview.image && (
                        <div className="w-32 h-24 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={linkPreview.image}
                            alt="Website preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      {/* Text Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="text-sm font-medium text-gray-900">Website Preview</h4>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, website: '' }));
                              setLinkPreview(null);
                            }}
                            className="text-gray-400 hover:text-gray-600 text-sm flex-shrink-0"
                          >
                            ×
                          </button>
                        </div>

                        <div className="flex gap-2 items-start">
                          {linkPreview.favicon && (
                            <img
                              src={linkPreview.favicon}
                              alt="Favicon"
                              className="w-4 h-4 rounded flex-shrink-0 mt-0.5"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate text-sm">
                              {linkPreview.title || formData.website}
                            </p>
                            {linkPreview.description && (
                              <p className="text-xs text-gray-600 line-clamp-2 mt-1">
                                {linkPreview.description}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 truncate mt-1">{formData.website}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-2 text-primary-600 hover:text-primary-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add a tag..."
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={handleAddTag}>
                Add
              </Button>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
              Event Image
            </label>
            <input
              id="image"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
            {imagePreview && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Preview:</p>
                <div className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-gray-200">
                  <img
                    src={imagePreview}
                    alt="Event preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, image: null }));
                      setImagePreview(null);
                    }}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6">
            <Button
              type="submit"
              disabled={loading || !!timeError}
              className="min-w-[120px]"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span>Creating...</span>
                </div>
              ) : (
                'Create Event'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}